import { configurationMap } from './auth-configs'
import type { CountryConfigMap } from './auth-config-types'
import { naEntraExternalIdPreproductionConfig } from './auth-configs/na-entra-external-id-preproduction.config'
import { naEntraExternalIdProductionConfig } from './auth-configs/na-entra-external-id-production.config'

/** GBPE auth waypoint path for Production (relative to site origin). */
export const GBPE_PRODUCTION_AUTH_WAYPOINT = '/_gbpe/code/prod1/auth-waypoint.html'

/** GBPE auth waypoint path for Preproduction / QA (relative to site origin). */
export const GBPE_PREPRODUCTION_AUTH_WAYPOINT = '/_gbpe/code/qa/auth-waypoint.html'

/** OAuth (MSAL) application id and B2C user-flow policy for one locale within a region. */
export interface GbpeLocaleAuthConfig {
	clientId: string
	policy: string
	authority: string
}

/** Region (ISO-style country code) → language code → auth identity. */
export type GbpeRegionAuthConfigs = Record<string, Record<string, GbpeLocaleAuthConfig>>

/**
 * OAuth client IDs and B2C policies for every region/locale in the static configuration map,
 * split by deployment environment. Matches {@link configurationMap} (data-attribute auth path).
 */
export interface GbpeAllRegionAuthConfigs {
	Production: GbpeRegionAuthConfigs
	Preproduction: GbpeRegionAuthConfigs
	/** Auth waypoint HTML paths per environment (relative to the host site). */
	waypoints: {
		Production: string
		Preproduction: string
	}
	/** North America Entra External ID (parallel to B2C in {@link configurationMap}). */
	entraExternalIdNorthAmerica: {
		Production: GbpeRegionAuthConfigs
		Preproduction: GbpeRegionAuthConfigs
	}
}

function projectCountryMap(map: CountryConfigMap): GbpeRegionAuthConfigs {
	const out: GbpeRegionAuthConfigs = {}
	for (const [country, langMap] of Object.entries(map)) {
		if (country === 'DEFAULT') {
			continue
		}
		const byLang: Record<string, GbpeLocaleAuthConfig> = {}
		for (const [lang, cfg] of Object.entries(langMap)) {
			if (lang === 'DEFAULT' || !cfg.mapClientId) {
				continue
			}
			byLang[lang] = {
				clientId: cfg.mapClientId,
				policy: cfg.mapPolicy,
				authority: cfg.mapAuthority,
			}
		}
		if (Object.keys(byLang).length > 0) {
			out[country] = byLang
		}
	}
	return out
}

function projectNaEntraNorthAmerica(map: Pick<CountryConfigMap, 'US' | 'CA' | 'MX'>): GbpeRegionAuthConfigs {
	const out: GbpeRegionAuthConfigs = {}
	for (const [country, langMap] of Object.entries(map)) {
		const byLang: Record<string, GbpeLocaleAuthConfig> = {}
		for (const [lang, cfg] of Object.entries(langMap)) {
			if (!cfg.mapClientId) {
				continue
			}
			byLang[lang] = {
				clientId: cfg.mapClientId,
				policy: cfg.mapPolicy,
				authority: cfg.mapAuthority,
			}
		}
		if (Object.keys(byLang).length > 0) {
			out[country] = byLang
		}
	}
	return out
}

let cached: GbpeAllRegionAuthConfigs | undefined

/**
 * Returns OAuth client IDs and B2C policies for all regions/locales known to GBPE
 * for Production and Preproduction. Data is derived from the same maps as
 * {@link getConfiguration}; it does not reflect ad-hoc overrides from script tags.
 */
export function getAllRegionAuthConfigs(): GbpeAllRegionAuthConfigs {
	if (!cached) {
		cached = {
			Production: projectCountryMap(configurationMap.Production),
			Preproduction: projectCountryMap(configurationMap.Preproduction),
			waypoints: {
				Production: GBPE_PRODUCTION_AUTH_WAYPOINT,
				Preproduction: GBPE_PREPRODUCTION_AUTH_WAYPOINT,
			},
			entraExternalIdNorthAmerica: {
				Production: projectNaEntraNorthAmerica(naEntraExternalIdProductionConfig),
				Preproduction: projectNaEntraNorthAmerica(naEntraExternalIdPreproductionConfig),
			},
		}
	}
	return cached
}
